<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import {
  CATEGORIZED_SUBGENRES_FILTERS,
  formatCategoryName,
} from './iconFromTagInformationsShared'

import TagElement from '~/components/tag/element.vue'
import TagGridTemplate from '~/components/tag/gridTemplate.vue'

import { useGetMediaUrl } from '~/composables/useGetMediaUrl'

import type {
  CategoryType,
  SubGenreCategoryData,
} from './iconFromTagInformationsShared'
import type TagInformation from '~/types/tagInformations'

type Props = {
  imgSrc: string
  tagInformations: TagInformation[]
  categoryType: CategoryType
}

const props = defineProps<Props>()

const { getMediaUrl } = useGetMediaUrl()
const { t } = useI18n()
const runtimeImgSrc = getMediaUrl(props.imgSrc)

const hasAllTypedTag = computed(() => {
  return props.tagInformations.some(
    (tagInformation: TagInformation): boolean =>
      tagInformation.tag.name === '_all_',
  )
})

const subGenreCategories = computed(() => {
  const output = {} as Record<string, SubGenreCategoryData>
  Object.entries(CATEGORIZED_SUBGENRES_FILTERS).forEach(
    ([categoryKey, typeFilters]) => {
      const categorizedSubGenres = props.tagInformations.filter((tagInfo) =>
        (typeFilters as string[]).includes(tagInfo.typeName),
      )
      if (!categorizedSubGenres.length) return

      const subCategoryIcon = t(
        `influencer.profile.slug.universe.categories.${categoryKey}.icon`,
      ) as string
      const subCategoryNameRaw = t(
        `influencer.profile.slug.universe.categories.${categoryKey}.name`,
        categorizedSubGenres.length,
      ) as string
      const category = formatCategoryName(subCategoryNameRaw)
      output[category] = {
        icon: subCategoryIcon,
        tags: categorizedSubGenres,
      }
    },
  )
  return output
})
</script>

<template>
  <div>
    <div class="tw-mb-6">
      <div class="tw-flex tw-items-center">
        <img
          class="tw-mr-2 tw-h-6 tw-w-6 tw-object-contain tw-object-center"
          :class="{
            'md:tw-mr-4 md:tw-h-10 md:tw-w-10': categoryType === 'preferences',
          }"
          :src="runtimeImgSrc"
          alt=""
        />
        <div>
          <h2
            class="sm:tw-mb-1"
            :class="[
              categoryType === 'preferences'
                ? 'tw-text-base tw-font-medium'
                : 'tw-text-h6',
            ]"
          >
            <slot name="title" />
          </h2>
          <span class="tw-text-body-sm tw-hidden tw-text-discrete2 sm:tw-block">
            <slot name="subtitle" v-bind="{ hasAllTypedTag }" />
          </span>
        </div>
      </div>
      <span class="tw-text-body-sm tw-mt-2 tw-block sm:tw-hidden">
        <slot name="subtitle" v-bind="{ hasAllTypedTag }" />
      </span>
    </div>
    <ul
      v-if="categoryType === 'music-type'"
      class="tw-flex tw-flex-col tw-gap-6"
    >
      <li
        v-for="([categoryName, { icon, tags }], index) in Object.entries(
          subGenreCategories,
        )"
        :key="index"
        class="tw-grid-column tw-grid tw-gap-y-4 md:tw-grid-cols-[144px_1fr]"
      >
        <div class="tw-flex tw-gap-2 tw-pl-6 md:tw-mt-2">
          <i :class="`${icon} tw-text-icon-discrete2`" />
          <span class="tw-text-body-sm tw-font-medium">
            {{ categoryName }}
          </span>
        </div>
        <TagGridTemplate class="tw-mb-0">
          <TagElement
            v-for="(tagInformation, tagInfoIndex) in tags"
            :key="tagInfoIndex"
            :i18nkey="tagInformation.typeName"
            :readonly="true"
            :tag="tagInformation.tag"
          />
        </TagGridTemplate>
      </li>
    </ul>
    <TagGridTemplate v-else class="tw-mb-0">
      <TagElement
        v-for="(tagInformation, index) in tagInformations"
        :key="index"
        :i18nkey="tagInformation.typeName"
        :readonly="true"
        :tag="tagInformation.tag"
      />
    </TagGridTemplate>
    <slot name="bottom" />
  </div>
</template>
